import { getTranslatedEdgeLabel } from '../lib/i18n.js';
import { getNodeColorFromTags, getLabelColor, DEFAULT_NODE_COLOR_THREE } from '../lib/colorUtils.js';

// --- Interfaces ---

interface TagWithColor {
  id: number;
  color: string;
  name?: string;
}

interface GraphNode {
  slug: string;
  title: string;
  state?: string;
  tags?: TagWithColor[];
  focal?: boolean;
}

interface GraphEdge {
  source: string;
  target: string;
  label?: string | null;
  translations?: { lang: string; name: string }[];
}

interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

interface Vec2 {
  x: number;
  y: number;
}

interface ForceSettings {
  centerForce: number;
  linkForce: number;
  repelMultiplier: number;
  spreadMultiplier: number;
  tagForce: number;
}

interface VisualSettings {
  textFadeThreshold: number;
  nodeSizeMultiplier: number;
  linkThicknessMultiplier: number;
}

interface GraphSettings extends ForceSettings, VisualSettings {}

interface GraphRendererOptions {
  cameraZ?: number;
  onNodeClick?: (slug: string) => void;
  onEdgeClick?: (source: string, target: string) => void;
  graphSettings?: Partial<GraphSettings>;
}

interface TagOverlap {
  slugA: string;
  slugB: string;
  count: number;
}

interface LabelSprite {
  width: number;
  height: number;
  // Index 0 (sharp) is built eagerly; heavier blur levels are rendered on first
  // use (most labels never blur). null = not yet generated. See pickBlurLevel.
  blurLevels: (HTMLCanvasElement | null)[];
  text: string;
  colorCss: string;
}

interface NodeState {
  slug: string;
  title: string;
  state?: string;
  position: Vec2;
  baseRadius: number;
  scale: number;             // animated hover/focus scale
  opacity: number;           // animated for focus mode
  hasTag: boolean;
  tagColorNum: number;       // 0xRRGGBB
  tagColorHex: string;
  fillColor: number;         // current fill (animated toward emissive on hover)
  emissiveIntensity: number; // 0..1
  baseEmissive: number;
  visible: boolean;
  label: LabelSprite;
  labelBaseScale: number;    // per-node scale factor (focal/degree-based)
  labelScaleAnim: number;    // animated scale used in focus mode
  labelOpacityAnim: number;
  labelBlurT: number;        // 0 (sharp) .. 1 (heavy blur), set by updateTextFade
}

interface EdgeState {
  key: string;
  source: string;
  target: string;
  label: string | null;
  edgeData: GraphEdge;
  baseTubeRadius: number;
  baseColor: number;
  baseOpacity: number;
  // animated values
  currentColor: number;
  currentOpacity: number;
  // edge label (optional)
  labelSprite?: LabelSprite;
  labelOpacityAnim?: number;
  labelScaleAnim?: number;
  labelBlurT?: number;
}

export interface GraphRenderer {
  readonly isFocused: boolean;
  readonly focusedSlug: string | null;
  updateNodeState(slug: string, newState: string, newTags?: TagWithColor[]): void;
  dispose(): void;
  focusNode(slug: string, duration?: number): void;
  resetCamera(duration?: number): void;
  focusNodeFlat(slug: string, duration?: number): void;
  exitFocusMode(duration?: number): void;
  navigateFocus(direction: string): void;
  getSettings(): GraphSettings;
  updateForceSettings(newForceSettings: Partial<ForceSettings>): void;
  highlightNodes(slugs: string[] | null): void;
  updateVisualSettings(newVisualSettings: Partial<VisualSettings>): void;
}

// --- Constants ---

const COLORS = {
  void: 0x080806,
  dim: 0x5C584E,
  edge: 0x5A5548,
  edgeActive: 0xA89E8E,
  signal: 0xFF4F1A,
  chalk: 0xE8E4DB,
};

const SIGNAL_RGB = { r: 0xFF, g: 0x4F, b: 0x1A };
const CHALK_RGB = { r: 0xE8, g: 0xE4, b: 0xDB };

// Visual scale: number of screen pixels per world unit at zoom=1.
// Edges and label sprites scale through this so default settings look reasonable.
const EDGE_PX_PER_WORLD = 4;
// Label sprite world-size base divisor (matches the 3D code's 55 used as canvas/world ratio)
const LABEL_BASE_DIV = 55;

// --- Color helpers ---

function numToRgb(n: number): { r: number; g: number; b: number } {
  return { r: (n >> 16) & 0xFF, g: (n >> 8) & 0xFF, b: n & 0xFF };
}

function rgbToNum(r: number, g: number, b: number): number {
  return ((r & 0xFF) << 16) | ((g & 0xFF) << 8) | (b & 0xFF);
}

function rgbToCss(rgb: { r: number; g: number; b: number }, alpha = 1): string {
  return `rgba(${rgb.r | 0}, ${rgb.g | 0}, ${rgb.b | 0}, ${alpha})`;
}

function numToCss(n: number, alpha = 1): string {
  return rgbToCss(numToRgb(n), alpha);
}

function lerpRgb(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }, t: number) {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

function lerpNum(a: number, b: number, t: number): number {
  const ar = numToRgb(a);
  const br = numToRgb(b);
  const r = lerpRgb(ar, br, t);
  return rgbToNum(r.r, r.g, r.b);
}

// --- Seeded RNG for stable layouts ---

function mulberry32(seed: number): () => number {
  return function () {
    seed |= 0; seed = seed + 0x6D2B79F5 | 0;
    let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return hash;
}

// --- Force-directed layout (2D, identical algorithm to 3D minus z) ---

function layoutNodes(nodes: GraphNode[], edges: GraphEdge[], spread: number = 25, settings: Partial<ForceSettings> = {}): Map<string, Vec2> {
  const {
    centerForce = 0.995,
    linkForce = 0.02,
    repelMultiplier = 1.0,
    spreadMultiplier = 1.0,
    tagForce = 1.0,
  } = settings;

  const TAG_FORCE_BASE = 0.002;
  const effectiveTagForce = TAG_FORCE_BASE * tagForce;
  const effectiveSpread = spread * spreadMultiplier;
  const positions = new Map<string, Vec2>();

  for (const node of nodes) {
    const rng = mulberry32(hashCode(node.slug));
    positions.set(node.slug, {
      x: (rng() - 0.5) * effectiveSpread,
      y: (rng() - 0.5) * effectiveSpread,
    });
  }

  const adj = new Map<string, Set<string>>();
  for (const n of nodes) adj.set(n.slug, new Set());
  for (const e of edges) {
    adj.get(e.source)?.add(e.target);
    adj.get(e.target)?.add(e.source);
  }

  const tagOverlaps: TagOverlap[] = [];
  if (effectiveTagForce > 0) {
    const tagToSlugs = new Map<number, string[]>();
    for (const node of nodes) {
      if (node.tags && node.tags.length > 0) {
        for (const tag of node.tags) {
          if (!tagToSlugs.has(tag.id)) tagToSlugs.set(tag.id, []);
          tagToSlugs.get(tag.id)!.push(node.slug);
        }
      }
    }
    const pairCounts = new Map<string, number>();
    for (const [, slugs] of tagToSlugs) {
      for (let i = 0; i < slugs.length; i++) {
        for (let j = i + 1; j < slugs.length; j++) {
          const key = slugs[i] < slugs[j]
            ? slugs[i] + '|' + slugs[j]
            : slugs[j] + '|' + slugs[i];
          pairCounts.set(key, (pairCounts.get(key) || 0) + 1);
        }
      }
    }
    for (const [key, count] of pairCounts) {
      const [slugA, slugB] = key.split('|');
      tagOverlaps.push({ slugA, slugB, count });
    }
  }

  // Edge-edge midpoint repulsion below is O(edges²) per iteration; it dominates
  // layout cost on dense graphs for little visual gain, so gate it to small graphs.
  const doEdgeEdgeRepulsion = edges.length <= 120;

  const iterations = 50;
  const slugs = [...positions.keys()]; // node set is stable across iterations — hoist out
  for (let iter = 0; iter < iterations; iter++) {
    const alpha = 1 - iter / iterations;

    for (let i = 0; i < slugs.length; i++) {
      for (let j = i + 1; j < slugs.length; j++) {
        const a = positions.get(slugs[i])!;
        const b = positions.get(slugs[j])!;
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
        const linked = adj.get(slugs[i])?.has(slugs[j]);
        const strength = (linked ? 120 : 600) * repelMultiplier;
        const force = (strength * alpha) / (dist * dist);
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        a.x += fx; a.y += fy;
        b.x -= fx; b.y -= fy;
      }
    }

    for (const e of edges) {
      const a = positions.get(e.source);
      const b = positions.get(e.target);
      if (!a || !b) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
      const force = dist * linkForce * alpha;
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      a.x += fx; a.y += fy;
      b.x -= fx; b.y -= fy;
    }

    for (const { slugA, slugB, count } of tagOverlaps) {
      const a = positions.get(slugA);
      const b = positions.get(slugB);
      if (!a || !b) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
      const force = dist * effectiveTagForce * count * alpha;
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      a.x += fx; a.y += fy;
      b.x -= fx; b.y -= fy;
    }

    // Edge-edge midpoint repulsion (avoid crossing tangles) — quadratic, small graphs only
    if (doEdgeEdgeRepulsion) {
      for (let i = 0; i < edges.length; i++) {
        for (let j = i + 1; j < edges.length; j++) {
          const e1 = edges[i];
          const e2 = edges[j];
          const a1 = positions.get(e1.source);
          const b1 = positions.get(e1.target);
          const a2 = positions.get(e2.source);
          const b2 = positions.get(e2.target);
          if (!a1 || !b1 || !a2 || !b2) continue;

          if (e1.source === e2.source || e1.source === e2.target ||
              e1.target === e2.source || e1.target === e2.target) continue;

          const mid1 = { x: (a1.x + b1.x) / 2, y: (a1.y + b1.y) / 2 };
          const mid2 = { x: (a2.x + b2.x) / 2, y: (a2.y + b2.y) / 2 };

          const dx = mid1.x - mid2.x;
          const dy = mid1.y - mid2.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
          const edgeRepulsion = (150 * alpha) / (dist * dist);
          const fx = (dx / dist) * edgeRepulsion;
          const fy = (dy / dist) * edgeRepulsion;

          a1.x += fx; a1.y += fy;
          b1.x += fx; b1.y += fy;
          a2.x -= fx; a2.y -= fy;
          b2.x -= fx; b2.y -= fy;
        }
      }
    }

    for (const pos of positions.values()) {
      pos.x *= centerForce;
      pos.y *= centerForce;
    }
  }

  return positions;
}

// --- Label sprite generation (depth-of-field blur levels) ---

function drawLabelToCanvas(text: string, colorCss: string, blurAmount: number = 0): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const fontSize = 64;
  const fontWeight = 500;
  const fontStr = `${fontWeight} ${fontSize}px Plus Jakarta Sans, Noto Sans SC, sans-serif`;
  ctx.font = fontStr;
  const metrics = ctx.measureText(text);
  const shadowPad = 20;
  const blurPad = blurAmount > 0 ? Math.ceil(blurAmount * 2) : 0;
  const totalPad = shadowPad + blurPad;
  const width = metrics.width + 24 + totalPad * 2;
  const height = fontSize + 24 + totalPad * 2;

  canvas.width = width;
  canvas.height = height;

  if (blurAmount > 0) ctx.filter = `blur(${blurAmount}px)`;

  ctx.font = fontStr;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.fillStyle = 'rgba(8, 8, 6, 1)';
  ctx.shadowColor = 'rgba(8, 8, 6, 1)';
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  ctx.shadowBlur = 18;
  ctx.fillText(text, width / 2, height / 2);
  ctx.fillText(text, width / 2, height / 2);

  ctx.shadowBlur = 10;
  ctx.fillText(text, width / 2, height / 2);
  ctx.fillText(text, width / 2, height / 2);

  ctx.shadowBlur = 4;
  ctx.fillText(text, width / 2, height / 2);
  ctx.fillText(text, width / 2, height / 2);

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.fillStyle = colorCss;
  ctx.fillText(text, width / 2, height / 2);

  return canvas;
}

const BLUR_AMOUNTS = [0, 3, 8, 16];

function makeLabelSprite(text: string, colorCss: string): LabelSprite {
  // Only the sharp level is generated up front; blurred variants are created on
  // demand in pickBlurLevel. Cuts canvas allocations + font rasterization ~4x,
  // since most on-screen labels stay sharp near the view center.
  const sharp = drawLabelToCanvas(text, colorCss, BLUR_AMOUNTS[0]);
  return {
    width: sharp.width,
    height: sharp.height,
    blurLevels: [sharp, null, null, null],
    text,
    colorCss,
  };
}

// --- Geometry helpers ---

function distancePointToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) {
    return Math.hypot(px - ax, py - ay);
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

// --- Renderer ---

export function createGraphRenderer(container: HTMLElement, graphData: GraphData, options: GraphRendererOptions = {}): GraphRenderer {
  let disposed = false;
  // When nothing is animating (no camera move, hover settle, focus glow, or active
  // transition) the render loop idles instead of redrawing every label each frame.
  // Interaction handlers and transition steps set this to force a redraw.
  let needsRender = true;

  // Settings
  const initSettings = options.graphSettings || {};
  let forceSettings: ForceSettings = {
    centerForce: initSettings.centerForce ?? 0.995,
    linkForce: initSettings.linkForce ?? 0.02,
    repelMultiplier: initSettings.repelMultiplier ?? 1.0,
    spreadMultiplier: initSettings.spreadMultiplier ?? 1.0,
    tagForce: initSettings.tagForce ?? 1.0,
  };
  let visualSettings: VisualSettings = {
    textFadeThreshold: initSettings.textFadeThreshold ?? 1,
    nodeSizeMultiplier: initSettings.nodeSizeMultiplier ?? 1.0,
    linkThicknessMultiplier: initSettings.linkThicknessMultiplier ?? 1.0,
  };

  let width = container.clientWidth;
  let height = container.clientHeight || 500;

  // Canvas
  const canvas = document.createElement('canvas');
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.display = 'block';
  canvas.style.background = '#080806';
  canvas.style.touchAction = 'none';
  container.appendChild(canvas);
  const ctx = canvas.getContext('2d')!;

  function applyDpi(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.floor(width * dpr));
    canvas.height = Math.max(1, Math.floor(height * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  applyDpi();

  // Camera (pan + zoom)
  let cameraCenter: Vec2 = { x: 0, y: 0 };
  let zoom = 1.0;
  let zoomTarget = 1.0; // for animated zoom transitions
  let cameraTarget: Vec2 = { x: 0, y: 0 }; // for animated pan transitions

  // Layout
  const nodePositions = layoutNodes(graphData.nodes, graphData.edges, 25, forceSettings);

  // Compute degree
  const degreeCounts = new Map<string, number>();
  for (const node of graphData.nodes) degreeCounts.set(node.slug, 0);
  for (const edge of graphData.edges) {
    degreeCounts.set(edge.source, (degreeCounts.get(edge.source) || 0) + 1);
    degreeCounts.set(edge.target, (degreeCounts.get(edge.target) || 0) + 1);
  }

  // Compute auto-fit zoom: fit the graph's bounding circle into view with padding.
  // options.cameraZ acts as a "minimum effective camera distance" — i.e., a max zoom clamp,
  // matching the 3D version's behavior of preserving a comfortable framing for tiny graphs.
  function computeFitZoom(): number {
    let maxR = 0;
    for (const pos of nodePositions.values()) {
      const r = Math.sqrt(pos.x * pos.x + pos.y * pos.y);
      if (r > maxR) maxR = r;
    }
    maxR = Math.max(maxR + 4, 8);
    const padding = 60; // screen-space padding
    const half = Math.min(width, height) / 2;
    let z = Math.max(half - padding, 50) / maxR;
    // Clamp to a reasonable maximum so tiny graphs don't fill the screen
    const minDist = options.cameraZ || 20;
    const maxZoom = Math.max(half - padding, 50) / Math.max(minDist * 0.4, 8);
    if (z > maxZoom) z = maxZoom;
    return z;
  }

  zoom = computeFitZoom();
  zoomTarget = zoom;

  // --- Build node and edge state ---

  const nodeStates = new Map<string, NodeState>();
  const edgeStates = new Map<string, EdgeState>();

  for (const node of graphData.nodes) {
    const pos = nodePositions.get(node.slug);
    if (!pos) continue;

    const tagColorNum = getNodeColorFromTags(node.tags as TagWithColor[]) as number;
    const tagColorHex = getNodeColorFromTags(node.tags as TagWithColor[], 'hex') as string;
    const hasTag = !!(node.tags && node.tags.length > 0);
    const isFocal = !!node.focal;
    const degree = degreeCounts.get(node.slug) || 0;

    let baseRadius: number;
    if (isFocal) {
      baseRadius = 2.0;
    } else {
      const baseR = 0.5;
      const degreeBonus = Math.min(Math.log2(degree + 1) * 0.85, 5.0);
      baseRadius = baseR + degreeBonus;
    }

    const baseEmissive = hasTag ? 0.4 : 0.2;
    const labelColorHex = getLabelColor(tagColorHex);
    const labelBaseScale = isFocal ? 1.4 : 0.7 + Math.min(Math.log2(degree + 1) * 0.3, 1.2);
    const label = makeLabelSprite(node.title, labelColorHex);

    nodeStates.set(node.slug, {
      slug: node.slug,
      title: node.title,
      state: node.state,
      position: { x: pos.x, y: pos.y },
      baseRadius,
      scale: 1.0,
      opacity: 1.0,
      hasTag,
      tagColorNum,
      tagColorHex,
      fillColor: tagColorNum,
      emissiveIntensity: baseEmissive,
      baseEmissive,
      visible: true,
      label,
      labelBaseScale,
      labelScaleAnim: 1.0,
      labelOpacityAnim: 1.0,
      labelBlurT: 0,
    });
  }

  for (const edge of graphData.edges) {
    const a = nodeStates.get(edge.source);
    const b = nodeStates.get(edge.target);
    if (!a || !b) continue;

    const bothActivated = a.state === 'activated' && b.state === 'activated';
    const baseTubeRadius = bothActivated ? 0.14 : 0.08;
    const baseColor = bothActivated ? COLORS.edgeActive : COLORS.edge;
    const baseOpacity = bothActivated ? 0.8 : 0.5;

    const key = `${edge.source}-${edge.target}`;
    const state: EdgeState = {
      key,
      source: edge.source,
      target: edge.target,
      label: edge.label ?? null,
      edgeData: edge,
      baseTubeRadius,
      baseColor,
      baseOpacity,
      currentColor: baseColor,
      currentOpacity: baseOpacity,
    };

    if (edge.label) {
      const translatedLabel = getTranslatedEdgeLabel({ label: edge.label, translations: edge.translations }) as string;
      state.labelSprite = makeLabelSprite(translatedLabel, numToCss(COLORS.edgeActive));
      state.labelScaleAnim = 0.5;
      state.labelOpacityAnim = 1.0;
      state.labelBlurT = 0;
    }

    edgeStates.set(key, state);
  }

  // --- Hover state ---

  let hoveredSlug: string | null = null;
  let hoveredEdgeKey: string | null = null;
  const HOVER_LERP = 0.08;
  const HOVER_SCALE = 1.15;
  const HOVER_LABEL_SCALE = 1.2;
  const HOVER_EMISSIVE = 0.6;
  const HOVER_EDGE_OPACITY = 1.0;

  // Focus mode state
  let isFocusMode = false;
  let focusedNodeSlug: string | null = null;

  // Saved state for filter/restore (highlightNodes)
  let savedPositions: Map<string, Vec2> | null = null;
  let savedCameraCenter: Vec2 | null = null;
  let savedZoom: number | null = null;

  // --- Coordinate transforms ---

  function worldToScreen(p: Vec2): Vec2 {
    return {
      x: (p.x - cameraCenter.x) * zoom + width / 2,
      y: (p.y - cameraCenter.y) * zoom + height / 2,
    };
  }

  function screenToWorld(s: Vec2): Vec2 {
    return {
      x: (s.x - width / 2) / zoom + cameraCenter.x,
      y: (s.y - height / 2) / zoom + cameraCenter.y,
    };
  }

  // --- Pointer handling ---

  let pointerDownPos: { x: number; y: number } | null = null;
  let pointerLastPos: { x: number; y: number } | null = null;
  let isDragging = false;
  const DRAG_THRESHOLD = 5;

  canvas.addEventListener('pointerdown', (event: PointerEvent) => {
    pointerDownPos = { x: event.clientX, y: event.clientY };
    pointerLastPos = { x: event.clientX, y: event.clientY };
    isDragging = false;
    canvas.setPointerCapture(event.pointerId);
  });

  canvas.addEventListener('pointermove', (event: PointerEvent) => {
    if (pointerLastPos && pointerDownPos && (event.buttons & 1)) {
      const dxFromDown = event.clientX - pointerDownPos.x;
      const dyFromDown = event.clientY - pointerDownPos.y;
      if (!isDragging && (dxFromDown * dxFromDown + dyFromDown * dyFromDown > DRAG_THRESHOLD * DRAG_THRESHOLD)) {
        isDragging = true;
      }
      if (isDragging) {
        const dx = event.clientX - pointerLastPos.x;
        const dy = event.clientY - pointerLastPos.y;
        cameraCenter.x -= dx / zoom;
        cameraCenter.y -= dy / zoom;
        cameraTarget.x = cameraCenter.x;
        cameraTarget.y = cameraCenter.y;
        needsRender = true; // drag sets center==target, so updateCamera won't detect it
      }
      pointerLastPos = { x: event.clientX, y: event.clientY };
    }
    handleHover(event);
  });

  canvas.addEventListener('pointerup', (event: PointerEvent) => {
    canvas.releasePointerCapture(event.pointerId);
    pointerLastPos = null;
    pointerDownPos = null;
    // isDragging gets reset on next pointerdown
  });

  canvas.addEventListener('pointerleave', () => {
    hoveredSlug = null;
    hoveredEdgeKey = null;
    canvas.style.cursor = 'default';
  });

  canvas.addEventListener('click', (event: MouseEvent) => {
    if (isDragging) {
      isDragging = false;
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const screenPt = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    const worldPt = screenToWorld(screenPt);

    // Hit-test nodes
    for (const [slug, node] of nodeStates) {
      if (!node.visible || node.opacity < 0.1) continue;
      const dx = worldPt.x - node.position.x;
      const dy = worldPt.y - node.position.y;
      const r = node.baseRadius * node.scale * visualSettings.nodeSizeMultiplier;
      if (dx * dx + dy * dy <= r * r) {
        if (options.onNodeClick) options.onNodeClick(slug);
        return;
      }
    }

    // Hit-test edges (wider hit zone, ~0.6 world units like 3D version)
    if (options.onEdgeClick) {
      const hitRadius = Math.max(0.6, 6 / zoom);
      for (const [, edge] of edgeStates) {
        const a = nodeStates.get(edge.source);
        const b = nodeStates.get(edge.target);
        if (!a || !b || !a.visible || !b.visible || edge.currentOpacity < 0.1) continue;
        const d = distancePointToSegment(worldPt.x, worldPt.y, a.position.x, a.position.y, b.position.x, b.position.y);
        if (d <= hitRadius) {
          options.onEdgeClick(edge.source, edge.target);
          return;
        }
      }
    }
  });

  function handleHover(event: PointerEvent): void {
    if (isDragging) return;
    const rect = canvas.getBoundingClientRect();
    const screenPt = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    const worldPt = screenToWorld(screenPt);

    let foundNode: string | null = null;
    for (const [slug, node] of nodeStates) {
      if (!node.visible || node.opacity < 0.1) continue;
      const dx = worldPt.x - node.position.x;
      const dy = worldPt.y - node.position.y;
      const r = node.baseRadius * node.scale * visualSettings.nodeSizeMultiplier;
      if (dx * dx + dy * dy <= r * r) {
        foundNode = slug;
        break;
      }
    }

    if (foundNode) {
      hoveredSlug = foundNode;
      hoveredEdgeKey = null;
      canvas.style.cursor = 'pointer';
      return;
    }
    hoveredSlug = null;

    let foundEdge: string | null = null;
    const hitRadius = Math.max(0.6, 6 / zoom);
    for (const [key, edge] of edgeStates) {
      const a = nodeStates.get(edge.source);
      const b = nodeStates.get(edge.target);
      if (!a || !b || !a.visible || !b.visible || edge.currentOpacity < 0.1) continue;
      const d = distancePointToSegment(worldPt.x, worldPt.y, a.position.x, a.position.y, b.position.x, b.position.y);
      if (d <= hitRadius) {
        foundEdge = key;
        break;
      }
    }

    if (foundEdge) {
      hoveredEdgeKey = foundEdge;
      canvas.style.cursor = 'pointer';
    } else {
      hoveredEdgeKey = null;
      canvas.style.cursor = 'default';
    }
  }

  // Wheel zoom (anchor at cursor)
  canvas.addEventListener('wheel', (event: WheelEvent) => {
    event.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const mx = event.clientX - rect.left;
    const my = event.clientY - rect.top;
    const before = screenToWorld({ x: mx, y: my });
    const factor = event.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.1, Math.min(50, zoom * factor));
    zoom = newZoom;
    zoomTarget = newZoom;
    const after = screenToWorld({ x: mx, y: my });
    cameraCenter.x += before.x - after.x;
    cameraCenter.y += before.y - after.y;
    cameraTarget.x = cameraCenter.x;
    cameraTarget.y = cameraCenter.y;
    needsRender = true; // zoom applied directly to center/target; updateCamera sees no delta
  }, { passive: false });

  // --- Animations ---

  // Returns true while any hover/emissive/color/opacity value is still animating,
  // so the render loop knows whether it can idle. Values snap to target within
  // epsilon (integer color lerps otherwise never settle exactly, wedging the loop).
  const SETTLE = 0.001;
  function colorSettled(cur: number, target: number): boolean {
    const c = numToRgb(cur), t = numToRgb(target);
    return Math.abs(c.r - t.r) <= 1 && Math.abs(c.g - t.g) <= 1 && Math.abs(c.b - t.b) <= 1;
  }

  function updateHoverAnimations(): boolean {
    let moving = false;
    for (const [slug, node] of nodeStates) {
      const isHovered = slug === hoveredSlug;
      const isFocusedNode = isFocusMode && slug === focusedNodeSlug;

      // Scale lerp (skipped in focus mode — focus mode manages its own)
      if (!isFocusMode) {
        const targetScale = isHovered ? HOVER_SCALE : 1.0;
        node.scale += (targetScale - node.scale) * HOVER_LERP;
        if (Math.abs(targetScale - node.scale) < SETTLE) node.scale = targetScale;
        else moving = true;
      }

      // Emissive intensity (focused node breathing managed by updateFocusGlow)
      if (!isFocusedNode) {
        const targetEmissive = isHovered ? HOVER_EMISSIVE : node.baseEmissive;
        node.emissiveIntensity += (targetEmissive - node.emissiveIntensity) * HOVER_LERP;
        if (Math.abs(targetEmissive - node.emissiveIntensity) < SETTLE) node.emissiveIntensity = targetEmissive;
        else moving = true;
      }

      // Emissive color: lerp toward Signal on hover, base otherwise
      const target = (isFocusedNode || isHovered)
        ? COLORS.signal
        : (node.tagColorNum || DEFAULT_NODE_COLOR_THREE);
      if (node.fillColor !== target) {
        node.fillColor = lerpNum(node.fillColor, target, HOVER_LERP);
        if (colorSettled(node.fillColor, target)) node.fillColor = target;
        else moving = true;
      }
    }

    for (const [key, edge] of edgeStates) {
      const isHovered = key === hoveredEdgeKey;
      const targetOpacity = isHovered ? HOVER_EDGE_OPACITY : edge.baseOpacity;
      edge.currentOpacity += (targetOpacity - edge.currentOpacity) * HOVER_LERP;
      if (Math.abs(targetOpacity - edge.currentOpacity) < SETTLE) edge.currentOpacity = targetOpacity;
      else moving = true;
      const targetColor = isHovered ? COLORS.signal : edge.baseColor;
      if (edge.currentColor !== targetColor) {
        edge.currentColor = lerpNum(edge.currentColor, targetColor, HOVER_LERP);
        if (colorSettled(edge.currentColor, targetColor)) edge.currentColor = targetColor;
        else moving = true;
      }
    }
    return moving;
  }

  // --- Focus glow (pulsing breathing effect) ---

  let focusGlowPulse = 0;
  function updateFocusGlow(): void {
    if (!isFocusMode || !focusedNodeSlug) return;
    const node = nodeStates.get(focusedNodeSlug);
    if (!node) return;
    focusGlowPulse = Math.sin(Date.now() * 0.0015) * 0.5 + 0.5;
    // Mild emissive boost on the focused node
    node.emissiveIntensity = node.baseEmissive + 0.15 + focusGlowPulse * 0.1;
  }

  // --- Camera animation (lerp toward target each frame) ---

  function updateCamera(): boolean {
    const dx = cameraTarget.x - cameraCenter.x;
    const dy = cameraTarget.y - cameraCenter.y;
    const dz = zoomTarget - zoom;
    if (Math.abs(dx) < 0.005 && Math.abs(dy) < 0.005 && Math.abs(dz) < 0.0002) {
      cameraCenter.x = cameraTarget.x;
      cameraCenter.y = cameraTarget.y;
      zoom = zoomTarget;
      return false;
    }
    cameraCenter.x += dx * 0.08;
    cameraCenter.y += dy * 0.08;
    zoom += dz * 0.08;
    return true;
  }

  // --- Text fade based on screen-space distance from camera center ---

  // Shallow depth-of-field analog: labels fade and blur with screen-space distance from view center.
  // Combined with zoom (zoomScaleFactor) this produces a similar feel to the 3D version.
  let zoomScaleFactor = 1.0;
  function updateTextFade(): void {
    if (isFocusMode) return; // focus mode manages its own opacity/scale

    // Reference zoom for natural label scaling
    const refZoom = computeFitZoom();
    // Gentle sqrt scaling: when zoomed out beyond fit, labels grow slightly so they stay readable
    zoomScaleFactor = zoom < refZoom
      ? Math.min(Math.sqrt(refZoom / Math.max(zoom, 0.01)), 2.5)
      : 1.0;

    // textFadeThreshold maps to a radius multiplier in screen pixels
    const distMul = Math.pow(2, visualSettings.textFadeThreshold * 2);
    const fadeStart = 220 * distMul;
    const fadeEnd = 700 * distMul;
    const minOpacity = 0.08;

    const cx = width / 2;
    const cy = height / 2;

    for (const node of nodeStates.values()) {
      const screenPt = worldToScreen(node.position);
      const d = Math.hypot(screenPt.x - cx, screenPt.y - cy);
      let opacity = 1.0;
      let blurT = 0;
      if (d > fadeStart) {
        const t = Math.min((d - fadeStart) / (fadeEnd - fadeStart), 1);
        const tSq = t * t;
        opacity = minOpacity + (1.0 - minOpacity) * (1.0 - tSq);
        blurT = t;
      }
      node.labelOpacityAnim = opacity;
      node.labelBlurT = blurT;
    }

    for (const edge of edgeStates.values()) {
      if (!edge.labelSprite) continue;
      const a = nodeStates.get(edge.source);
      const b = nodeStates.get(edge.target);
      if (!a || !b) continue;
      const mid = { x: (a.position.x + b.position.x) / 2, y: (a.position.y + b.position.y) / 2 };
      const screenPt = worldToScreen(mid);
      const d = Math.hypot(screenPt.x - cx, screenPt.y - cy);
      let opacity = 1.0;
      let blurT = 0;
      if (d > fadeStart) {
        const t = Math.min((d - fadeStart) / (fadeEnd - fadeStart), 1);
        const tSq = t * t;
        opacity = minOpacity + (1.0 - minOpacity) * (1.0 - tSq);
        blurT = t;
      }
      edge.labelOpacityAnim = opacity;
      edge.labelBlurT = blurT;
    }
  }

  function pickBlurLevel(blurT: number, sprite: LabelSprite): HTMLCanvasElement {
    const levels = sprite.blurLevels;
    const idx = Math.min(Math.floor(blurT * levels.length), levels.length - 1);
    let level = levels[idx];
    if (!level) {
      level = drawLabelToCanvas(sprite.text, sprite.colorCss, BLUR_AMOUNTS[idx]);
      levels[idx] = level;
    }
    return level;
  }

  // --- Render ---

  // Reused across frames to avoid allocating a Map + a Vec2 per node every frame
  // (that per-frame garbage was a measurable GC cost). Keys mirror nodeStates,
  // which is fixed for the renderer's lifetime.
  const screenPos = new Map<string, Vec2>();

  function render(): void {
    // Background
    ctx.fillStyle = numToCss(COLORS.void);
    ctx.fillRect(0, 0, width, height);

    // Frustum (with margin so labels just outside the canvas still render)
    const cullMargin = 200;
    const cullL = -cullMargin;
    const cullT = -cullMargin;
    const cullR = width + cullMargin;
    const cullB = height + cullMargin;

    // Project screen positions into the reused map (in place, no allocation)
    if (screenPos.size === 0) {
      for (const s of nodeStates.keys()) screenPos.set(s, { x: 0, y: 0 });
    }
    for (const [s, n] of nodeStates) {
      if (!n.visible) continue;
      const p = screenPos.get(s)!;
      p.x = (n.position.x - cameraCenter.x) * zoom + width / 2;
      p.y = (n.position.y - cameraCenter.y) * zoom + height / 2;
    }

    // Edges first (under nodes)
    ctx.lineCap = 'round';
    for (const [, edge] of edgeStates) {
      const a = nodeStates.get(edge.source);
      const b = nodeStates.get(edge.target);
      if (!a || !b || !a.visible || !b.visible) continue;
      if (edge.currentOpacity < 0.005) continue;

      const sa = screenPos.get(edge.source)!;
      const sb = screenPos.get(edge.target)!;
      // Cull edges fully outside the visible rect
      if ((sa.x < cullL && sb.x < cullL) || (sa.x > cullR && sb.x > cullR) ||
          (sa.y < cullT && sb.y < cullT) || (sa.y > cullB && sb.y > cullB)) continue;

      let lineWidthPx = edge.baseTubeRadius * 2 * visualSettings.linkThicknessMultiplier * EDGE_PX_PER_WORLD * zoom * 0.5;
      if (isFocusMode && focusedNodeSlug && (edge.source === focusedNodeSlug || edge.target === focusedNodeSlug)) {
        lineWidthPx *= 2.0;
      }
      lineWidthPx = Math.max(lineWidthPx, 0.5);

      ctx.strokeStyle = numToCss(edge.currentColor, edge.currentOpacity);
      ctx.lineWidth = lineWidthPx;
      ctx.beginPath();
      ctx.moveTo(sa.x, sa.y);
      ctx.lineTo(sb.x, sb.y);
      ctx.stroke();
    }

    // Nodes
    for (const [, node] of nodeStates) {
      if (!node.visible || node.opacity < 0.005) continue;
      const s = screenPos.get(node.slug)!;
      const baseR = node.baseRadius * visualSettings.nodeSizeMultiplier;
      const r = baseR * node.scale * zoom;
      if (r < 0.4) continue;
      // Cull nodes whose halo bounds are off-screen
      const haloMax = r * 2.0;
      if (s.x + haloMax < cullL || s.x - haloMax > cullR ||
          s.y + haloMax < cullT || s.y - haloMax > cullB) continue;

      const fillRgb = numToRgb(node.fillColor);
      // Blend toward Chalk by emissive intensity to simulate the "glowing" effect
      const blend = Math.min(node.emissiveIntensity, 1.0);
      const lit = lerpRgb(fillRgb, CHALK_RGB, Math.min(blend * 0.4, 0.5));

      // Skip expensive radial-gradient halos when nodes are too small to perceive them.
      const haloable = r >= 2.5;

      if (haloable && node.hasTag) {
        const halo = r * 1.6;
        const grad = ctx.createRadialGradient(s.x, s.y, r * 0.9, s.x, s.y, halo);
        const baseColor = numToRgb(node.tagColorNum);
        grad.addColorStop(0, rgbToCss(baseColor, 0.18 * node.opacity));
        grad.addColorStop(1, rgbToCss(baseColor, 0));
        ctx.fillStyle = grad;
        ctx.fillRect(s.x - halo, s.y - halo, halo * 2, halo * 2);
      }

      if (haloable && node.emissiveIntensity > node.baseEmissive + 0.05) {
        const extraGlow = (node.emissiveIntensity - node.baseEmissive) / (HOVER_EMISSIVE - node.baseEmissive + 0.001);
        const halo = r * (1.6 + extraGlow * 0.4);
        const grad = ctx.createRadialGradient(s.x, s.y, r, s.x, s.y, halo);
        grad.addColorStop(0, rgbToCss(SIGNAL_RGB, 0.25 * extraGlow * node.opacity));
        grad.addColorStop(1, rgbToCss(SIGNAL_RGB, 0));
        ctx.fillStyle = grad;
        ctx.fillRect(s.x - halo, s.y - halo, halo * 2, halo * 2);
      }

      // Focus glow ring on focused node (pulsing)
      if (isFocusMode && node.slug === focusedNodeSlug) {
        const glowScale = 1.0 + focusGlowPulse * 0.05;
        const glowR = r * 1.25 * glowScale;
        const glowOpacity = (0.04 + focusGlowPulse * 0.04) * node.opacity;
        const glowColor = lerpRgb(SIGNAL_RGB, CHALK_RGB, 0.35);
        const grad = ctx.createRadialGradient(s.x, s.y, r, s.x, s.y, glowR * 1.3);
        grad.addColorStop(0, rgbToCss(glowColor, glowOpacity * 6));
        grad.addColorStop(1, rgbToCss(glowColor, 0));
        ctx.fillStyle = grad;
        ctx.fillRect(s.x - glowR * 1.3, s.y - glowR * 1.3, glowR * 2.6, glowR * 2.6);
      }

      ctx.globalAlpha = node.opacity;
      ctx.fillStyle = `rgb(${lit.r | 0}, ${lit.g | 0}, ${lit.b | 0})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Edge labels
    for (const [, edge] of edgeStates) {
      if (!edge.labelSprite) continue;
      const a = nodeStates.get(edge.source);
      const b = nodeStates.get(edge.target);
      if (!a || !b || !a.visible || !b.visible) continue;
      const opacity = edge.labelOpacityAnim ?? 1.0;
      if (opacity < 0.05) continue;

      const sa = screenPos.get(edge.source)!;
      const sb = screenPos.get(edge.target)!;
      const s = { x: (sa.x + sb.x) / 2, y: (sa.y + sb.y) / 2 };
      if (s.x < cullL || s.x > cullR || s.y < cullT || s.y > cullB) continue;
      const baseScale = edge.labelScaleAnim ?? 0.5;
      const scaleFinal = baseScale * (isFocusMode ? 1.0 : zoomScaleFactor);
      const w = edge.labelSprite.width / LABEL_BASE_DIV * EDGE_PX_PER_WORLD * scaleFinal;
      const h = edge.labelSprite.height / LABEL_BASE_DIV * EDGE_PX_PER_WORLD * scaleFinal;
      const blurT = edge.labelBlurT ?? 0;
      const tex = pickBlurLevel(blurT, edge.labelSprite);

      ctx.globalAlpha = opacity;
      ctx.drawImage(tex, s.x - w / 2, s.y - h / 2, w, h);
      ctx.globalAlpha = 1;
    }

    // Node labels (drawn last, on top of everything)
    for (const [, node] of nodeStates) {
      if (!node.visible || node.opacity < 0.01) continue;
      const opacity = (node.labelOpacityAnim ?? 1.0) * Math.min(node.opacity, 1.0);
      if (opacity < 0.05) continue;

      const s = screenPos.get(node.slug)!;
      if (s.x < cullL || s.x > cullR || s.y < cullT || s.y > cullB) continue;
      const baseR = node.baseRadius * visualSettings.nodeSizeMultiplier;
      const r = baseR * node.scale * zoom;
      const labelOffset = r + 18;

      const hoverMul = (!isFocusMode && node.slug === hoveredSlug) ? HOVER_LABEL_SCALE : 1.0;
      const focusMul = isFocusMode ? node.labelScaleAnim : 1.0;
      const baseLabelScale = node.labelBaseScale;
      const overall = baseLabelScale * (isFocusMode ? focusMul : zoomScaleFactor) * hoverMul;
      const w = node.label.width / LABEL_BASE_DIV * EDGE_PX_PER_WORLD * overall;
      const h = node.label.height / LABEL_BASE_DIV * EDGE_PX_PER_WORLD * overall;

      const tex = pickBlurLevel(node.labelBlurT ?? 0, node.label);

      ctx.globalAlpha = opacity;
      ctx.drawImage(tex, s.x - w / 2, s.y - labelOffset - h / 2, w, h);
      ctx.globalAlpha = 1;
    }
  }

  // --- Animation loop ---

  let animId: number;
  function animate(): void {
    animId = requestAnimationFrame(animate);
    const cameraMoving = updateCamera();
    const hoverMoving = updateHoverAnimations();
    // Focus mode has a continuously pulsing glow, so it always needs frames.
    if (isFocusMode) updateFocusGlow();
    if (needsRender || cameraMoving || hoverMoving || isFocusMode) {
      updateTextFade();
      render();
      needsRender = false;
    }
  }
  animate();

  // --- Resize ---

  const resizeObserver = new ResizeObserver(() => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;
    width = w;
    height = h;
    applyDpi();
    needsRender = true;
  });
  resizeObserver.observe(container);

  // --- Public API ---

  return {
    get isFocused() { return isFocusMode; },
    get focusedSlug() { return focusedNodeSlug; },

    updateNodeState(slug: string, newState: string, newTags?: TagWithColor[]): void {
      const node = nodeStates.get(slug);
      if (!node) return;

      const newColor = newTags ? getNodeColorFromTags(newTags) as number : node.tagColorNum;
      const newColorHex = newTags ? getNodeColorFromTags(newTags, 'hex') as string : node.tagColorHex;
      const hasTag = newTags ? newTags.length > 0 : node.hasTag;
      const newEmissive = hasTag ? 0.4 : 0.2;

      node.state = newState;
      node.tagColorNum = newColor;
      node.tagColorHex = newColorHex;
      node.hasTag = hasTag;
      node.fillColor = newColor;
      node.baseEmissive = newEmissive;
      node.emissiveIntensity = newEmissive;

      // Rebuild label with new color
      const labelColorHex = getLabelColor(newColorHex);
      node.label = makeLabelSprite(node.title, labelColorHex);

      // Sync graphData node so subsequent lookups reflect the change
      const nodeData = graphData.nodes.find(n => n.slug === slug);
      if (nodeData) {
        nodeData.state = newState;
        if (newTags) nodeData.tags = newTags;
      }
      needsRender = true;
    },

    dispose(): void {
      disposed = true;
      cancelAnimationFrame(animId);
      resizeObserver.disconnect();
      // Canvas removal handles all GPU/CPU memory implicitly
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      nodeStates.clear();
      edgeStates.clear();
    },

    focusNode(slug: string, duration: number = 1000): void {
      const node = nodeStates.get(slug);
      if (!node) return;
      animateCameraTo(node.position, Math.max(zoom, computeFitZoom() * 1.5), duration);
    },

    resetCamera(duration: number = 1000): void {
      animateCameraTo({ x: 0, y: 0 }, computeFitZoom(), duration);
    },

    focusNodeFlat(slug: string, duration: number = 1000): void {
      const focal = nodeStates.get(slug);
      if (!focal) return;

      isFocusMode = true;
      focusedNodeSlug = slug;

      const connectedSlugs = new Set<string>();
      connectedSlugs.add(slug);
      for (const edge of graphData.edges) {
        if (edge.source === slug) connectedSlugs.add(edge.target);
        if (edge.target === slug) connectedSlugs.add(edge.source);
      }

      // Capture starting positions
      const originalPositions = new Map<string, Vec2>();
      for (const [s, n] of nodeStates) originalPositions.set(s, { x: n.position.x, y: n.position.y });

      // Center the focus cluster at the current camera center (analogous to "in front of camera")
      const focusCenter: Vec2 = { x: cameraCenter.x, y: cameraCenter.y };
      const focusRadius = 10;
      const targetPositions = new Map<string, Vec2>();
      const connected = Array.from(connectedSlugs).filter(s => s !== slug);
      const n = connected.length;

      // Deterministic angle offset per slug
      let angleOffset = 0;
      if (n > 1) {
        let h = 0;
        for (let i = 0; i < slug.length; i++) {
          h = ((h << 5) - h) + slug.charCodeAt(i);
          h = h & h;
        }
        angleOffset = (Math.abs(h) % 360) * (Math.PI / 180);
      }

      connected.forEach((nodeSlug, index) => {
        const baseAngle = (index / n) * Math.PI * 2 + angleOffset;
        const jitter = ((Math.abs(nodeSlug.charCodeAt(0)) % 30) - 15) * (Math.PI / 180);
        const angle = baseAngle + jitter;
        targetPositions.set(nodeSlug, {
          x: focusCenter.x + Math.cos(angle) * focusRadius,
          y: focusCenter.y + Math.sin(angle) * focusRadius,
        });
      });
      targetPositions.set(slug, { x: focusCenter.x, y: focusCenter.y });

      // Push unrelated nodes far off-screen (they'll fade out)
      for (const [nodeSlug, n2] of nodeStates) {
        if (!connectedSlugs.has(nodeSlug)) {
          targetPositions.set(nodeSlug, { x: n2.position.x, y: n2.position.y - 30 });
        }
      }

      const startTime = Date.now();

      function animateToFlat(): void {
        if (disposed) return;
        needsRender = true;
        const elapsed = Date.now() - startTime;
        const t = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);

        for (const [nodeSlug, node] of nodeStates) {
          const target = targetPositions.get(nodeSlug);
          const original = originalPositions.get(nodeSlug);
          if (target && original) {
            node.position.x = original.x + (target.x - original.x) * eased;
            node.position.y = original.y + (target.y - original.y) * eased;
          }

          const isConnected = connectedSlugs.has(nodeSlug);
          const isFocused = nodeSlug === slug;
          const targetOpacity = isFocused ? 1.0 : isConnected ? 1.0 : 0.01;
          node.opacity += (targetOpacity - node.opacity) * 0.1;

          let targetScale = 1.0;
          if (isFocused) targetScale = 2.5;
          else if (isConnected) targetScale = 1.2;
          node.scale += (targetScale - node.scale) * 0.1;

          // Label scale follows node
          let targetLabelScale = 0.1;
          if (isFocused) targetLabelScale = 0.8;
          else if (isConnected) targetLabelScale = 0.7;
          node.labelScaleAnim += (targetLabelScale - node.labelScaleAnim) * 0.1;
          node.labelOpacityAnim += (targetOpacity - node.labelOpacityAnim) * 0.1;
        }

        // Edges fade based on connection to focused node
        for (const [, edge] of edgeStates) {
          const touchesFocused = edge.source === slug || edge.target === slug;
          const bothConnected = connectedSlugs.has(edge.source) && connectedSlugs.has(edge.target);
          let targetOpacity: number;
          let targetColor: number;
          if (touchesFocused) {
            targetOpacity = 1.0;
            targetColor = COLORS.signal;
          } else if (bothConnected) {
            targetOpacity = 0.15;
            targetColor = edge.baseColor;
          } else {
            targetOpacity = 0.0;
            targetColor = edge.baseColor;
          }
          edge.currentOpacity += (targetOpacity - edge.currentOpacity) * 0.1;
          edge.currentColor = lerpNum(edge.currentColor, targetColor, 0.1);

          if (edge.labelSprite) {
            let labelOpacity: number;
            let labelScale: number;
            if (touchesFocused) {
              labelOpacity = 1.0; labelScale = 0.75;
            } else if (bothConnected) {
              labelOpacity = 0.1; labelScale = 0.5;
            } else {
              labelOpacity = 0.0; labelScale = 0.1;
            }
            edge.labelOpacityAnim = (edge.labelOpacityAnim ?? 1.0) + (labelOpacity - (edge.labelOpacityAnim ?? 1.0)) * 0.1;
            edge.labelScaleAnim = (edge.labelScaleAnim ?? 0.5) + (labelScale - (edge.labelScaleAnim ?? 0.5)) * 0.1;
          }
        }

        if (t < 1) requestAnimationFrame(animateToFlat);
      }

      animateToFlat();
    },

    exitFocusMode(duration: number = 1000): void {
      if (!isFocusMode) return;
      isFocusMode = false;
      focusedNodeSlug = null;

      // Re-run layout for fresh positions
      const newPositions = layoutNodes(graphData.nodes, graphData.edges, 25, forceSettings);
      for (const [s, p] of newPositions) {
        nodePositions.set(s, p);
      }

      const startPositions = new Map<string, Vec2>();
      for (const [s, n] of nodeStates) startPositions.set(s, { x: n.position.x, y: n.position.y });

      const startTime = Date.now();
      const startCam = { x: cameraCenter.x, y: cameraCenter.y };
      const startZoom = zoom;
      const targetCam: Vec2 = { x: 0, y: 0 };
      const targetZoom = computeFitZoom();
      cameraTarget = { ...targetCam };
      zoomTarget = targetZoom;

      function animateBack(): void {
        if (disposed) return;
        needsRender = true;
        const elapsed = Date.now() - startTime;
        const t = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);

        for (const [s, n] of nodeStates) {
          const target = newPositions.get(s);
          const start = startPositions.get(s);
          if (target && start) {
            n.position.x = start.x + (target.x - start.x) * eased;
            n.position.y = start.y + (target.y - start.y) * eased;
          }
          n.opacity += (1.0 - n.opacity) * 0.1;
          n.scale += (1.0 - n.scale) * 0.1;
          n.labelScaleAnim += (1.0 - n.labelScaleAnim) * 0.1;
          n.labelOpacityAnim += (1.0 - n.labelOpacityAnim) * 0.1;
        }

        for (const [, edge] of edgeStates) {
          edge.currentOpacity += (edge.baseOpacity - edge.currentOpacity) * 0.1;
          edge.currentColor = lerpNum(edge.currentColor, edge.baseColor, 0.1);
          if (edge.labelSprite) {
            edge.labelOpacityAnim = (edge.labelOpacityAnim ?? 1.0) + (1.0 - (edge.labelOpacityAnim ?? 1.0)) * 0.1;
            edge.labelScaleAnim = (edge.labelScaleAnim ?? 0.5) + (0.5 - (edge.labelScaleAnim ?? 0.5)) * 0.1;
          }
        }

        cameraCenter.x = startCam.x + (targetCam.x - startCam.x) * eased;
        cameraCenter.y = startCam.y + (targetCam.y - startCam.y) * eased;
        zoom = startZoom + (targetZoom - startZoom) * eased;

        if (t < 1) {
          requestAnimationFrame(animateBack);
        } else {
          // Ensure final state
          for (const [s, n] of nodeStates) {
            const target = newPositions.get(s);
            if (target) { n.position.x = target.x; n.position.y = target.y; }
            n.opacity = 1.0;
            n.scale = 1.0;
            n.labelScaleAnim = 1.0;
            n.labelOpacityAnim = 1.0;
            n.emissiveIntensity = n.baseEmissive;
          }
          for (const [, edge] of edgeStates) {
            edge.currentOpacity = edge.baseOpacity;
            edge.currentColor = edge.baseColor;
            if (edge.labelSprite) {
              edge.labelOpacityAnim = 1.0;
              edge.labelScaleAnim = 0.5;
            }
          }
        }
      }

      animateBack();
    },

    navigateFocus(direction: string): void {
      if (!isFocusMode || !focusedNodeSlug) return;

      const neighbors: string[] = [];
      for (const edge of graphData.edges) {
        if (edge.source === focusedNodeSlug) neighbors.push(edge.target);
        if (edge.target === focusedNodeSlug) neighbors.push(edge.source);
      }
      if (neighbors.length === 0) return;

      const focused = nodeStates.get(focusedNodeSlug);
      if (!focused) return;
      const focusScreen = worldToScreen(focused.position);
      // Normalize so up is +y in screen ndc — for our screen coords +y is down,
      // so to keep arrow-up = "up on screen" we flip y here.
      const dirVectors: Record<string, Vec2> = {
        ArrowRight: { x: 1, y: 0 },
        ArrowLeft:  { x: -1, y: 0 },
        ArrowUp:    { x: 0, y: -1 },
        ArrowDown:  { x: 0, y: 1 },
      };
      const dir = dirVectors[direction];
      if (!dir) return;

      let bestSlug: string | null = null;
      let bestScore = Infinity;
      for (const s of neighbors) {
        const n = nodeStates.get(s);
        if (!n) continue;
        const screen = worldToScreen(n.position);
        const dx = screen.x - focusScreen.x;
        const dy = screen.y - focusScreen.y;
        const dot = dx * dir.x + dy * dir.y;
        if (dot <= 0.001) continue;
        const perpDist = Math.abs(dx * dir.y - dy * dir.x);
        const dist = Math.sqrt(dx * dx + dy * dy);
        const score = perpDist * 2 + dist;
        if (score < bestScore) { bestScore = score; bestSlug = s; }
      }

      if (bestSlug) {
        this.focusNodeFlat(bestSlug);
        if (options.onNodeClick) options.onNodeClick(bestSlug);
      }
    },

    getSettings(): GraphSettings {
      return { ...forceSettings, ...visualSettings };
    },

    updateForceSettings(newForceSettings: Partial<ForceSettings>): void {
      forceSettings = { ...forceSettings, ...newForceSettings };

      const newPositions = layoutNodes(graphData.nodes, graphData.edges, 25, forceSettings);
      const startPositions = new Map<string, Vec2>();
      for (const [s, n] of nodeStates) startPositions.set(s, { x: n.position.x, y: n.position.y });

      const duration = 800;
      const startTime = Date.now();

      function animateRelay(): void {
        if (disposed) return;
        needsRender = true;
        const elapsed = Date.now() - startTime;
        const t = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);

        for (const [s, n] of nodeStates) {
          const start = startPositions.get(s);
          const target = newPositions.get(s);
          if (start && target) {
            n.position.x = start.x + (target.x - start.x) * eased;
            n.position.y = start.y + (target.y - start.y) * eased;
          }
        }

        if (t < 1) {
          requestAnimationFrame(animateRelay);
        } else {
          for (const [s, p] of newPositions) nodePositions.set(s, p);
        }
      }
      animateRelay();
    },

    highlightNodes(slugs: string[] | null): void {
      needsRender = true; // visibility changes below must paint even if no animation runs
      const matchSet = slugs && slugs.length > 0 ? new Set(slugs) : null;
      const isFiltered = !!matchSet;

      // Save originals on first filter
      if (isFiltered && !savedPositions) {
        savedPositions = new Map<string, Vec2>();
        for (const [s, n] of nodeStates) savedPositions.set(s, { x: n.position.x, y: n.position.y });
        savedCameraCenter = { x: cameraCenter.x, y: cameraCenter.y };
        savedZoom = zoom;
      }

      // Compute target positions
      let targetPositions: Map<string, Vec2> | undefined;
      if (isFiltered) {
        const subNodes = graphData.nodes.filter(n => matchSet!.has(n.slug));
        const subEdges = graphData.edges.filter(e => matchSet!.has(e.source) && matchSet!.has(e.target));
        const ratio = Math.sqrt(subNodes.length / Math.max(graphData.nodes.length, 1));
        const subSpread = Math.max(25 * ratio, 5);
        targetPositions = layoutNodes(subNodes, subEdges, subSpread, forceSettings);
      } else if (savedPositions) {
        targetPositions = savedPositions;
      }

      // Visibility
      for (const [s, n] of nodeStates) {
        n.visible = !isFiltered || matchSet!.has(s);
      }
      for (const [, edge] of edgeStates) {
        const show = !isFiltered || (matchSet!.has(edge.source) && matchSet!.has(edge.target));
        // Visibility tracked by source/target node visibility — render() guards on it
        // Use an opacity ramp instead of a hard visible flag for edges so they fade nicely
        if (!show) edge.currentOpacity = 0;
      }

      if (!targetPositions) return;

      const startPositions = new Map<string, Vec2>();
      for (const [s, n] of nodeStates) {
        if (n.visible) startPositions.set(s, { x: n.position.x, y: n.position.y });
      }

      // Camera framing: bounding circle of targetPositions
      let camFinalCenter: Vec2;
      let camFinalZoom: number;
      if (isFiltered) {
        const center: Vec2 = { x: 0, y: 0 };
        let count = 0;
        for (const [, p] of targetPositions) { center.x += p.x; center.y += p.y; count++; }
        if (count > 0) { center.x /= count; center.y /= count; }
        let maxR = 0;
        for (const [, p] of targetPositions) {
          const r = Math.hypot(p.x - center.x, p.y - center.y);
          if (r > maxR) maxR = r;
        }
        maxR = Math.max(maxR + 4, 8);
        const padding = 60;
        const half = Math.min(width, height) / 2;
        camFinalCenter = center;
        camFinalZoom = Math.max(half - padding, 50) / maxR;
      } else {
        camFinalCenter = savedCameraCenter ?? { x: 0, y: 0 };
        camFinalZoom = savedZoom ?? computeFitZoom();
      }

      const startCam = { x: cameraCenter.x, y: cameraCenter.y };
      const startZoomLocal = zoom;
      const duration = 600;
      const startTime = Date.now();

      function animateFilter(): void {
        if (disposed) return;
        needsRender = true;
        const elapsed = Date.now() - startTime;
        const t = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);

        for (const [s, n] of nodeStates) {
          if (!n.visible) continue;
          const start = startPositions.get(s);
          const target = targetPositions!.get(s);
          if (start && target) {
            n.position.x = start.x + (target.x - start.x) * eased;
            n.position.y = start.y + (target.y - start.y) * eased;
          }
        }

        cameraCenter.x = startCam.x + (camFinalCenter.x - startCam.x) * eased;
        cameraCenter.y = startCam.y + (camFinalCenter.y - startCam.y) * eased;
        zoom = startZoomLocal + (camFinalZoom - startZoomLocal) * eased;
        cameraTarget = { x: cameraCenter.x, y: cameraCenter.y };
        zoomTarget = zoom;

        if (t < 1) {
          requestAnimationFrame(animateFilter);
        } else {
          for (const [s, n] of nodeStates) {
            if (n.visible && targetPositions!.has(s)) {
              const p = targetPositions!.get(s)!;
              nodePositions.set(s, { x: p.x, y: p.y });
            }
          }
          // Restore edge opacity for visible edges
          for (const [, edge] of edgeStates) {
            const a = nodeStates.get(edge.source);
            const b = nodeStates.get(edge.target);
            if (a && b && a.visible && b.visible) {
              edge.currentOpacity = edge.baseOpacity;
            }
          }
        }
      }

      animateFilter();

      // Restore edge opacity for visible edges immediately so they animate from base
      for (const [, edge] of edgeStates) {
        const a = nodeStates.get(edge.source);
        const b = nodeStates.get(edge.target);
        if (a && b && a.visible && b.visible) {
          // ramp back from 0 toward baseOpacity via animation (it's set as currentOpacity above when not visible)
          if (edge.currentOpacity === 0) edge.currentOpacity = 0.001;
        }
      }

      if (!isFiltered) {
        savedPositions = null;
        savedCameraCenter = null;
        savedZoom = null;
      }
    },

    updateVisualSettings(newVisualSettings: Partial<VisualSettings>): void {
      visualSettings = { ...visualSettings, ...newVisualSettings };
      // Node sizes, link thickness, and text fade are computed per-frame from
      // visualSettings, so no rebuild is needed — just force a repaint.
      needsRender = true;
    },
  };

  // --- Internal: animated camera tween ---
  function animateCameraTo(center: Vec2, targetZ: number, duration: number): void {
    const startTime = Date.now();
    const startCenter = { x: cameraCenter.x, y: cameraCenter.y };
    const startZoomLocal = zoom;
    cameraTarget = { ...center };
    zoomTarget = targetZ;
    function step(): void {
      if (disposed) return;
      needsRender = true;
      const elapsed = Date.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      cameraCenter.x = startCenter.x + (center.x - startCenter.x) * eased;
      cameraCenter.y = startCenter.y + (center.y - startCenter.y) * eased;
      zoom = startZoomLocal + (targetZ - startZoomLocal) * eased;
      if (t < 1) requestAnimationFrame(step);
    }
    step();
  }
}
